import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import Information from 'mdi-react/InformationIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';
import { noop } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';
import { TextInput, Radio } from '@prisjakt/ui-inputs';
import CurrencySettings from '@prisjakt/ui-currency/src/market-settings';

import { isValidEmail } from '../../../account/helpers';

import CtxLink from '../../../../components/render-context/ctx-link';
import PriceTrend from '../price-trend';

import { LoginPromoButton, Section, NumberInput, Error } from '../../styles';
import * as S from './styles';

const getDefaultPriceThreshold = (
  product,
  minimumStepValue = 0.1,
  numberOfDecimals = 2
) => {
  const price = product.priceSummary.regular;
  return price
    ? (price - Math.max(price * 0.01, minimumStepValue)).toFixed(
        numberOfDecimals
      )
    : null;
};

const STATUS_IN_STOCK = 'in_stock';

const alertTypes = {
  IN_STOCK: 'IN_STOCK',
  PRICE_DROPS: 'PRICE_DROPS',
  IN_STOCK_PRICE_DROPS: 'IN_STOCK_PRICE_DROPS',
};

const radioSelections = {
  IN_STOCK: 'IN_STOCK',
  PRICE_DROPS: 'PRICE_DROPS',
  PRICE_BELOW: 'PRICE_BELOW',
};

const ERROR_EMAIL_INVALID = 'error.email.invalid';
const ERROR_EMAIL_IN_USE = 'alert.email-associated-with-user';
const ERROR_TRESHOLD_TOO_HIGH = 'alert.threshold-too-high';
const ERROR_PREDICTED_THRESHOLD = 'alert-form.predicted-threshold-too-low.md';
const ERROR_UNKNOWN = 'alert.error';

class ProductAlertForm extends React.PureComponent {
  constructor(props) {
    super(props);

    const productInStock =
      props.product && props.product.stockStatus === STATUS_IN_STOCK;
    const { minimumStepValue, numberOfDecimals } = CurrencySettings[
      this.props.context.market.code
    ];

    let radioSelection;
    let onlyAlertIfInStock = true;

    if (props.product.alert) {
      const alertType = props.product.alert.alertType;
      if (alertType === alertTypes.IN_STOCK) {
        radioSelection = radioSelections.IN_STOCK;
      } else if (props.product.alert.priceThreshold > 0) {
        radioSelection = radioSelections.PRICE_BELOW;
      } else {
        radioSelection = radioSelections.PRICE_DROPS;
      }
      if (alertType === alertTypes.PRICE_DROPS) {
        onlyAlertIfInStock = false;
      }
    } else if (productInStock) {
      radioSelection = radioSelections.PRICE_DROPS;
    } else {
      radioSelection = radioSelections.IN_STOCK;
    }

    this.state = {
      error: false,
      email: '',
      radioSelection,
      // Not sure if we want to disable type select ever?
      priceThreshold:
        props.product.alert && props.product.alert.priceThreshold
          ? props.product.alert.priceThreshold
          : getDefaultPriceThreshold(
              props.product,
              minimumStepValue,
              numberOfDecimals
            ),
      formKey: 0,
      onlyAlertIfInStock,
    };

    // Find the lowest predicted price in 3 months
    const timeNow = Math.round(new Date().getTime() / 1000);
    this.hasPricePrediction =
      props.product &&
      props.product.priceForecast &&
      props.product.priceForecast.length > 0;
    this.lowestPredictedPrice =
      this.hasPricePrediction &&
      props.product.priceForecast.find(
        price => timeNow + 7889229 < Date.parse(price.date) / 1000
      );
    this.lowestPredictedPrice = this.lowestPredictedPrice
      ? this.lowestPredictedPrice.price.toFixed(numberOfDecimals)
      : null;
  }

  clearErrors = () => {
    this.setState({ error: false });
  };

  setError = (error, value = true) => {
    this.setState({
      error: { [error]: value },
    });
  };

  handleInputChange = ({ target }) => {
    this.setState({
      [target.name]: target.type === 'checkbox' ? target.checked : target.value,
    });
  };

  static getDerivedStateFromProps(nextProps) {
    if (!nextProps.error) {
      return null;
    }

    switch (nextProps.error) {
      case ERROR_TRESHOLD_TOO_HIGH:
      case ERROR_PREDICTED_THRESHOLD:
      case ERROR_EMAIL_INVALID:
      case ERROR_EMAIL_IN_USE: {
        return {
          error: { [nextProps.error]: true },
        };
      }
      default: {
        return {
          error: { ERROR_UNKNOWN: true },
        };
      }
    }
  }

  componentDidMount() {
    if (this.emailInput && this.props.shouldAutoFocus) {
      this.emailInput.focus();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.priceThreshold !== this.state.priceThreshold) {
      if (
        this.state.type === alertTypes.PRICE_DROPS &&
        this.hasPricePrediction
      ) {
        if (
          this.lowestPredictedPrice < this.props.product.priceSummary.regular &&
          parseFloat(this.state.priceThreshold) < this.lowestPredictedPrice
        ) {
          this.setError(ERROR_PREDICTED_THRESHOLD);
        } else {
          this.clearErrors();
        }
      }
    }
  }

  saveAlert = event => {
    const { product, useEmail, userProductAlerts } = this.props;
    const userAlert = userProductAlerts.find(
      alert => alert.productId === product.id
    );

    event.preventDefault();

    if (!product) {
      return;
    }

    this.clearErrors();

    if (useEmail && !isValidEmail(this.state.email)) {
      this.setError(ERROR_EMAIL_INVALID);
      if (this.props.shouldAutoFocus) {
        this.emailInput.focus();
        this.emailInput.select();
      }
      return;
    }

    const {
      email,
      radioSelection,
      priceThreshold,
      onlyAlertIfInStock,
    } = this.state;

    const graphQLAlert = this.calculateGraphQLAlertType(
      radioSelection,
      priceThreshold,
      product,
      onlyAlertIfInStock
    );

    if (graphQLAlert) {
      this.props.onCreate({
        alertId: userAlert && userAlert.alertId,
        productId: product.id,
        email: useEmail ? email : undefined,
        ...graphQLAlert,
      });
    }
  };

  calculateGraphQLAlertType = (
    radioSelection,
    priceThreshold,
    product,
    onlyAlertIfInStock
  ) => {
    // Input
    // One of : IN_STOCK, PRICE_DROP, PRICE_BELOW
    // Output
    // { type: One of 'in_stock', 'price_drops', 'in_stock_price_drops', priceThreshold?: number  }
    switch (radioSelection) {
      case radioSelections.IN_STOCK:
        return { type: alertTypes.IN_STOCK };
      case radioSelections.PRICE_DROPS:
        return {
          type: onlyAlertIfInStock
            ? alertTypes.IN_STOCK_PRICE_DROPS
            : alertTypes.PRICE_DROPS,
        };
      case radioSelections.PRICE_BELOW:
        if (
          priceThreshold < 0 ||
          priceThreshold >= product.priceSummary.regular
        ) {
          this.setError(ERROR_TRESHOLD_TOO_HIGH);
          return null;
        }
        return {
          type: onlyAlertIfInStock
            ? alertTypes.IN_STOCK_PRICE_DROPS
            : alertTypes.PRICE_DROPS,
          priceThreshold,
        };
      default:
        throw new Error("This shouldn't happen");
    }
  };

  renderTypeOptions() {
    const { product, account, openLoginPromo } = this.props;
    const { radioSelection } = this.state;
    const { t } = this.props.context;
    const { isAuthenticated } = account || {};
    const productInStock = product && product.stockStatus === STATUS_IN_STOCK;
    const { minimumStepValue } = CurrencySettings[
      this.props.context.market.code
    ];
    return (
      <React.Fragment>
        <Section>
          {!productInStock && (
            <Radio
              name="radioSelection"
              id="alert-type-back-in-stock"
              label={t('alerts', 'type.back-in-stock')}
              value={radioSelections.IN_STOCK}
              defaultChecked={radioSelection === radioSelections.IN_STOCK}
              checked={radioSelection === radioSelections.IN_STOCK}
              onChange={this.handleInputChange}
            />
          )}

          <Radio
            name="radioSelection"
            id="alert-type-price-drop"
            label={t('alerts', 'type.price-drop')}
            value={radioSelections.PRICE_DROPS}
            defaultChecked={radioSelection === radioSelections.PRICE_DROPS}
            checked={radioSelection === radioSelections.PRICE_DROPS}
            onChange={this.handleInputChange}
          />

          <LoginPromoButton onClick={!isAuthenticated ? openLoginPromo : noop}>
            <Radio
              name="radioSelection"
              id="alert-type-price-below"
              label={t('alerts', 'type.price-below')}
              onChange={this.handleInputChange}
              disabled={!isAuthenticated}
              value={radioSelections.PRICE_BELOW}
              checked={radioSelection === radioSelections.PRICE_BELOW}
              defaultChecked={radioSelection === radioSelections.PRICE_BELOW}
            />
          </LoginPromoButton>
        </Section>

        {radioSelection === radioSelections.PRICE_BELOW && isAuthenticated && (
          <React.Fragment>
            <NumberInput
              name="priceThreshold"
              label={t('alerts', 'alert-form.price-limit')}
              disabled={!isAuthenticated}
              defaultValue={this.state.priceThreshold}
              onChange={this.handleInputChange}
              step={minimumStepValue}
              max={parseFloat(product.priceSummary.regular)}
              min={0}
            />

            {this.state.error && (
              <Error>
                <Information size={32} />

                {this.state.error[ERROR_TRESHOLD_TOO_HIGH] === true &&
                  t('alerts', 'alert-form.threshold-too-high')}

                {this.state.error[ERROR_PREDICTED_THRESHOLD] === true && (
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      this.setState(prevState => ({
                        priceThreshold: this.lowestPredictedPrice.toString(),

                        // HACK: By updating the key value of form we force a re-render of the form
                        // thus updating defaultValue of `priceThreshold` input field
                        // Should probably find a better way to do this, like allowing controlled inputs
                        formKey: prevState.formKey + 1,
                      }));
                    }}
                  >
                    <T domain="alerts" price={this.lowestPredictedPrice}>
                      alert-form.predicted-threshold-too-low.md
                    </T>
                  </button>
                )}
              </Error>
            )}
          </React.Fragment>
        )}
        {radioSelection !== radioSelections.IN_STOCK && (
          <S.Checkbox
            id="only-alert-if-in-stock-checkbox"
            name="onlyAlertIfInStock"
            checked={this.state.onlyAlertIfInStock}
            onChange={this.handleInputChange}
          >
            <T domain="alerts">alert-form.only-notify-in-stock</T>
          </S.Checkbox>
        )}
      </React.Fragment>
    );
  }

  render() {
    const {
      product,
      useEmail,
      showCancel,
      privacyPolicyUri,
      withoutGraph,
    } = this.props;
    const { t } = this.props.context;

    return (
      <S.AlertForm method="post" key={this.state.formKey}>
        {this.renderTypeOptions()}
        <S.EmailInput error={this.state.error[ERROR_EMAIL_INVALID] === true}>
          {useEmail && (
            <React.Fragment>
              <TextInput
                type="email"
                name="email"
                inputRef={ref => {
                  this.emailInput = ref;
                }}
                value={this.state.email}
                onChange={this.handleInputChange}
                placeholder={t('alerts', 'alert-form.email-placeholder')}
                tabIndex="0"
                autoFocus={this.props.shouldAutoFocus}
              />
              <S.EmailHint
                visible={this.state.error[ERROR_EMAIL_IN_USE] === true}
              >
                <T domain="alerts">alert-form.email-in-use-error</T>
                &nbsp;
                <CtxLink to="/login">
                  <T domain="alerts">alert-form.login-error</T>
                </CtxLink>
              </S.EmailHint>
              <S.EmailHint
                error
                visible={this.state.error[ERROR_EMAIL_INVALID] === true}
              >
                <T domain="alerts">alert-form.invalid-email-error</T>
              </S.EmailHint>
              <S.EmailHint
                error
                visible={this.state.error[ERROR_UNKNOWN] === true}
              >
                <T domain="alerts">alert-form.unknown-error</T>
              </S.EmailHint>
              {privacyPolicyUri && (
                <S.EmailHint visible discreetLink>
                  <CtxLink to={privacyPolicyUri}>
                    <T domain="product">alert.how-your-email-is-used</T>
                  </CtxLink>
                </S.EmailHint>
              )}
            </React.Fragment>
          )}
        </S.EmailInput>
        <div>
          {product.statistics && (
            <PriceTrend withoutGraph={withoutGraph} product={product} />
          )}

          <Button
            style={{ margin: '10px 0', width: '100%' }}
            color="primary"
            disabled={this.props.disabled}
            onClick={this.saveAlert}
            iconPosition="left"
            data-test="CreateAlertButton"
          >
            <T domain="alerts">alert-form.create-button</T>
          </Button>
        </div>
        {showCancel && product && (
          <S.CancelButton>
            <Link to={product && product.pathName}>
              <T domain="alerts">alert-form.cancel-button</T>
            </Link>
          </S.CancelButton>
        )}
      </S.AlertForm>
    );
  }
}

const ProductAlertFormContextWrapper = React.memo(props => {
  const context = React.useContext(AppContext);
  return <ProductAlertForm {...props} context={context} />;
});
ProductAlertForm.contextType = AppContext;

const stringOrNumberType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
]);

ProductAlertFormContextWrapper.propTypes = {
  product: PropTypes.shape({
    id: stringOrNumberType,
    alert: PropTypes.shape({
      id: stringOrNumberType,
      priceThreshold: PropTypes.number,
      alertType: PropTypes.string,
    }),
    priceSummary: PropTypes.shape({
      regular: stringOrNumberType,
    }),
    stockStatus: PropTypes.string,
    pathName: PropTypes.string,
    statistics: PropTypes.shape({
      nodes: PropTypes.array.isRequired,
      pageInfo: PropTypes.shape({
        lowestPrice: PropTypes.shape({
          date: PropTypes.string,
          price: PropTypes.number,
        }),
        since: PropTypes.string,
      }).isRequired,
    }),
    priceForecast: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  account: PropTypes.shape({
    me: PropTypes.object,
    isAuthenticated: PropTypes.bool,
  }),
  onRemove: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  privacyPolicyUri: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  useEmail: PropTypes.bool,
  showCancel: PropTypes.bool,
  alertType: PropTypes.oneOf(Object.keys(radioSelections)),
  shouldAutoFocus: PropTypes.bool,
  openLoginPromo: PropTypes.func,
  userProductAlerts: PropTypes.arrayOf(
    PropTypes.shape({ productId: PropTypes.number, alertId: PropTypes.number })
  ),
};

ProductAlertFormContextWrapper.defaultProps = {
  account: {
    me: undefined,
    isAuthenticated: false,
  },
  privacyPolicyUri: undefined,
  disabled: false,
  error: false,
  useEmail: true,
  showCancel: true,
  alertType: undefined,
  shouldAutoFocus: false,
  openLoginPromo: noop,
  userProductAlerts: [],
};

ProductAlertForm.propTypes = ProductAlertFormContextWrapper.propTypes;
ProductAlertForm.defaultProps = ProductAlertFormContextWrapper.defaultProps;

export default connect(state => ({
  account: state.areas.account,
  userProductAlerts: state.areas.sidebar.alerts.products,
}))(ProductAlertFormContextWrapper);
